"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'

export default function CreateHarmonogramPage() {
  const t = useT()
  const router = useRouter()
  const { organizationId, tenantId } = useOrganizationScopeDetail()

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'stationId',
      label: t('terminal_scheduling.fields.stationId', 'Station'),
      type: 'text',
      required: true,
      placeholder: 'Station UUID',
    },
    {
      id: 'slotType',
      label: t('terminal_scheduling.fields.slotType', 'Slot Type'),
      type: 'select',
      required: true,
      options: [
        { value: 'arrival', label: t('terminal_scheduling.slots.type.arrival', 'Arrival') },
        { value: 'departure', label: t('terminal_scheduling.slots.type.departure', 'Departure') },
        { value: 'shunting', label: t('terminal_scheduling.slots.type.shunting', 'Shunting') },
      ],
    },
    {
      id: 'trackNumber',
      label: t('terminal_scheduling.fields.trackNumber', 'Track Number'),
      type: 'number',
      required: true,
    },
    {
      id: 'platform',
      label: t('terminal_scheduling.fields.platform', 'Platform'),
      type: 'text',
    },
    {
      id: 'arrivalTime',
      label: t('terminal_scheduling.fields.arrivalTime', 'Arrival Time'),
      type: 'text',
      required: true,
      placeholder: 'HH:MM',
    },
    {
      id: 'departureTime',
      label: t('terminal_scheduling.fields.departureTime', 'Departure Time'),
      type: 'text',
      required: true,
      placeholder: 'HH:MM',
    },
    {
      id: 'validFrom',
      label: t('terminal_scheduling.fields.validFrom', 'Valid From'),
      type: 'date',
      required: true,
    },
    {
      id: 'validUntil',
      label: t('terminal_scheduling.fields.validUntil', 'Valid Until'),
      type: 'date',
    },
    {
      id: 'trainNumber',
      label: t('terminal_scheduling.fields.trainNumber', 'Train Number'),
      type: 'text',
    },
    {
      id: 'notes',
      label: t('terminal_scheduling.fields.notes', 'Notes'),
      type: 'textarea',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'location',
      title: t('terminal_scheduling.groups.location', 'Location'),
      fields: ['stationId', 'trackNumber', 'platform'],
    },
    {
      id: 'timing',
      title: t('terminal_scheduling.groups.timing', 'Timing'),
      fields: ['slotType', 'arrivalTime', 'departureTime', 'validFrom', 'validUntil'],
    },
    {
      id: 'details',
      title: t('terminal_scheduling.groups.details', 'Details'),
      fields: ['trainNumber', 'notes'],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('terminal_scheduling.harmonogramCreate.title', 'New Harmonogram Entry')}
          backHref="/backend/terminal-scheduling/harmonogram"
          fields={fields}
          groups={groups}
          submitLabel={t('terminal_scheduling.harmonogram.actions.create', 'New Entry')}
          cancelHref="/backend/terminal-scheduling/harmonogram"
          onSubmit={async (values) => {
            const payload = {
              ...values,
              daysOfWeek: [1, 2, 3, 4, 5],
              organizationId,
              tenantId,
            }
            const { result: created } = await createCrud<{ id?: string }>('terminal_scheduling/harmonogram', payload)
            flash(t('terminal_scheduling.harmonogram.flash.created', 'Harmonogram entry created'), 'success')
            router.push('/backend/terminal-scheduling/harmonogram')
          }}
        />
      </PageBody>
    </Page>
  )
}
